import { type PropsWithChildren, type ReactNode } from "react"
import { Alert, Button, Stack, Typography } from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from "react-error-boundary"
import { EmailLink, PhoneLink } from "@/components/atoms"
import { CUSTOMER_SERVICE_EMAIL, CUSTOMER_SERVICE_PHONE } from "@/features/customer-service"

function Fallback({ error, resetErrorBoundary, action }: FallbackProps & ErrorBoundaryProps) {
    const t = useTranslations("ErrorBoundary")

    return (
        <Alert severity="error">
            <Stack
                flexDirection="column"
                gap="sm"
            >
                <Typography
                    color="inherit"
                    textStyle="h4"
                >
                    {error.message}
                </Typography>
                <Typography
                    color="inherit"
                    wordBreak="break-word"
                    textAlign="start"
                >
                    {t.rich("customerService", {
                        emailLink: () => <EmailLink email={CUSTOMER_SERVICE_EMAIL} />,
                        phoneLink: () => <PhoneLink phone={CUSTOMER_SERVICE_PHONE} />,
                    })}
                </Typography>
                {action ?? (
                    <Button
                        onClick={resetErrorBoundary}
                        variant="contained"
                    >
                        {t("tryAgain")}
                    </Button>
                )}
            </Stack>
        </Alert>
    )
}

type ErrorBoundaryProps = { action?: ReactNode }

export function ErrorBoundary({ action, children }: PropsWithChildren<ErrorBoundaryProps>) {
    return (
        <ReactErrorBoundary
            FallbackComponent={(props) => (
                <Fallback
                    action={action}
                    {...props}
                />
            )}
            onError={(error, info) => {
                console.error(error, info.componentStack)
            }}
        >
            {children}
        </ReactErrorBoundary>
    )
}

ErrorBoundary.messages = ["ErrorBoundary"] as const
